import type {
  AnalysisRun,
  InspectResponse,
  Project,
  PublicConfig,
  GeneratedPrompt,
  CreateAnalysisRequest,
  CreateProjectRequest,
  UpdateProjectRequest,
  ContactRequest,
} from '@visiward/schemas';
import type { ErrorCode } from '@visiward/shared';

const BASE = import.meta.env.VITE_API_BASE_URL || '';

export class ApiError extends Error {
  constructor(
    readonly code: ErrorCode | 'NETWORK',
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

/** Resolver for the current Firebase ID token, set by the auth layer. */
let tokenProvider: () => Promise<string | null> = async () => null;
export function setTokenProvider(fn: () => Promise<string | null>): void {
  tokenProvider = fn;
}

async function request<T>(
  path: string,
  options: { method?: string; body?: unknown; auth?: boolean } = {},
): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (options.auth !== false) {
    const token = await tokenProvider();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method: options.method ?? 'GET',
      headers,
      body: options.body ? JSON.stringify(options.body) : undefined,
      credentials: 'include', // send/receive the anonymous session cookie
    });
  } catch {
    throw new ApiError('NETWORK', 'Could not reach the server. Please check your connection.', 0);
  }

  const text = await res.text();
  const data = text ? JSON.parse(text) : {};

  if (!res.ok) {
    const err = (data as { error?: { code: ErrorCode; message: string } }).error;
    throw new ApiError(
      err?.code ?? 'INTERNAL',
      err?.message ?? 'Something went wrong.',
      res.status,
    );
  }
  return data as T;
}

export const api = {
  getPublicConfig: () => request<PublicConfig>('/api/config/public', { auth: false }),

  inspectSite: (url: string, turnstileToken?: string) =>
    request<InspectResponse>('/api/site/inspect', {
      method: 'POST',
      body: { url, turnstileToken },
    }),

  generatePrompts: (body: {
    websiteUrl: string;
    brandName?: string | null;
    industry?: string;
    competitors?: string[];
  }) => request<{ prompts: GeneratedPrompt[] }>('/api/prompts/generate', { method: 'POST', body }),

  createAnalysis: (body: CreateAnalysisRequest) =>
    request<{ run: AnalysisRun; ownerType: 'anonymous' | 'user'; projectId?: string }>(
      '/api/analyses',
      { method: 'POST', body },
    ),

  getAnalysis: (runId: string) =>
    request<{ run: AnalysisRun; ownerType: 'anonymous' | 'user' }>(`/api/analyses/${runId}`),

  claimAnalysis: (runId: string, projectName?: string) =>
    request<{ run: AnalysisRun; projectId: string }>(`/api/analyses/${runId}/claim`, {
      method: 'POST',
      body: { projectName },
    }),

  listProjects: () => request<{ projects: Project[] }>('/api/projects'),

  createProject: (body: CreateProjectRequest) =>
    request<{ project: Project }>('/api/projects', { method: 'POST', body }),

  getProject: (id: string) => request<{ project: Project }>(`/api/projects/${id}`),

  updateProject: (id: string, body: UpdateProjectRequest) =>
    request<{ project: Project }>(`/api/projects/${id}`, { method: 'PATCH', body }),

  deleteProject: (id: string) =>
    request<{ ok: boolean }>(`/api/projects/${id}`, { method: 'DELETE' }),

  listProjectRuns: (id: string) =>
    request<{ runs: AnalysisRun[] }>(`/api/projects/${id}/runs`),

  sendContact: (body: ContactRequest) =>
    request<{ ok: boolean }>('/api/contact', { method: 'POST', body }),

  // ── Admin (requires an authenticated admin custom claim) ──
  admin: {
    getConfig: () => request<{ config: PublicConfig | null }>('/api/admin/config'),
    updateConfig: (body: Partial<PublicConfig>) =>
      request<{ ok: boolean }>('/api/admin/config', { method: 'PATCH', body }),
    listUsers: () => request<{ users: Record<string, unknown>[] }>('/api/admin/users'),
    listUsage: () => request<{ usage: Record<string, unknown>[] }>('/api/admin/usage'),
    listContact: () => request<{ requests: Record<string, unknown>[] }>('/api/admin/contact'),
    listWorkspaces: () =>
      request<{ workspaces: Record<string, unknown>[] }>('/api/admin/workspaces'),
  },
};
